import { Box, Typography, Grid } from '@mui/material';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { ThemePalette } from 'mui.theme';
import { formatNumberToShortScale } from 'views/Production/Loader/component/utils';
import {
  countUniqueTires,
  countUniqueTiresFromLeakage,
  calculatePotentialFuelSavings,
} from 'views/TirePressure/util';
import { getPercentageDiff } from 'views/Settings/ExternalUsers/Squares/DashboardV2/Components/AssetDetailsView/utils';
import { isTpmsOnlyCustomer } from 'utils/helpers/general';
import { type TPMSLeakageNotification } from 'types/notification.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { useAppSelector } from 'store/hook';

const DashboardCardsView = ({
  enableDarkTheme,
  notifications,
  prevNotifications,
  leakageNotifications = [],
  prevLeakageNotifications = [],
  data,
  fuelRate,
}: {
  enableDarkTheme: boolean;
  notifications: any[];
  prevNotifications: any[];
  leakageNotifications?: TPMSLeakageNotification[];
  prevLeakageNotifications?: TPMSLeakageNotification[];
  data: any;
  fuelRate: number;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const modules = useAppSelector((state) => state.authReducer.customer.modules);
  const fontColor = palette.textPrimary;

  // Count unique tires from both TPMS and leakage notifications
  const uniqueTiresFromTPMS = countUniqueTires(notifications) ?? 0;
  const uniqueTiresFromLeakage =
    countUniqueTiresFromLeakage(leakageNotifications);
  const uniqueTires = uniqueTiresFromTPMS + uniqueTiresFromLeakage;

  const prevUniqueTiresFromTPMS = countUniqueTires(prevNotifications) ?? 0;
  const prevUniqueTiresFromLeakage = countUniqueTiresFromLeakage(
    prevLeakageNotifications
  );
  const prevUniqueTires = prevUniqueTiresFromTPMS + prevUniqueTiresFromLeakage;
  const uniqueTiresDiff = getPercentageDiff(uniqueTires, prevUniqueTires);

  // Count unique assets from both sources
  const assetsFromTPMS = [
    ...new Set(notifications.map((item) => item?.assetId)),
  ];
  const assetsFromLeakage = [
    ...new Set(leakageNotifications.map((item) => item?.assetId)),
  ];
  const assetsWithWarning = new Set([...assetsFromTPMS, ...assetsFromLeakage])
    .size;

  const prevAssetsFromTPMS = [
    ...new Set(prevNotifications.map((item) => item?.assetId)),
  ];
  const prevAssetsFromLeakage = [
    ...new Set(prevLeakageNotifications.map((item) => item?.assetId)),
  ];
  const prevAssetsWithWarning = new Set([
    ...prevAssetsFromTPMS,
    ...prevAssetsFromLeakage,
  ]).size;
  const assetsWithWarningDiff = getPercentageDiff(
    assetsWithWarning,
    prevAssetsWithWarning
  );

  // Count total notifications from both sources
  const totalNotifications = notifications.length + leakageNotifications.length;
  const prevTotalNotifications =
    prevNotifications.length + prevLeakageNotifications.length;
  const totalNotificationsDiff = getPercentageDiff(
    totalNotifications,
    prevTotalNotifications
  );

  const fuelSaving = calculatePotentialFuelSavings(
    notifications,
    data,
    fuelRate
  );

  const prevFuelSaving = calculatePotentialFuelSavings(
    prevNotifications,
    data,
    fuelRate
  );

  const fuelSavingDiff = getPercentageDiff(
    fuelSaving?.totalFuelWasted ?? 0,
    prevFuelSaving?.totalFuelWasted ?? 0
  );

  let cardsData = [
    {
      title: t('tires_with_warning'),
      value: uniqueTires,
      diff: uniqueTiresDiff,
      isInverse: true,
    },
    {
      title: t('assets_with_warning'),
      value: assetsWithWarning,
      diff: assetsWithWarningDiff,
      isInverse: true,
    },
    {
      title: t('total_notifications'),
      value: totalNotifications,
      diff: totalNotificationsDiff,
      isInverse: true,
    },
    {
      id: 'costSaved',
      title: t('cost_saved'),
      value: fuelSaving ? fuelSaving?.totalFuelWasted : 'N/A',
      diff: fuelSavingDiff,
      unit: '$',
      isInverse: true,
    },
  ];

  // Hide "Cost Saved" for tpms-only-customers [as we don't have fuel data for gdy, lhl, sav]
  if (isTpmsOnlyCustomer(modules)) {
    cardsData = cardsData.filter((card) => card.id !== 'costSaved');
  }

  return (
    <Box
      sx={{
        marginTop: '10px',
      }}
    >
      <Grid container spacing={2}>
        {cardsData.map((item, index) => {
          const isNegativeDiff = item.diff < 0;
          return (
            <Grid
              item
              lg={12 / cardsData.length}
              sm={12}
              md={6}
              xs={12}
              key={index}
            >
              <Box
                sx={{
                  border: '1px solid #2F445E',
                  borderRadius: '8px',
                  padding: '16px',
                  minHeight: '110px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      color: fontColor,
                      fontSize: '14px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '10px',
                  }}
                >
                  {isNegativeDiff ? (
                    <SouthEastIcon
                      sx={{
                        color: item.isInverse ? '#009161' : '#EB5757',
                        fontSize: 30,
                        marginRight: '3px',
                      }}
                    />
                  ) : (
                    <NorthEastIcon
                      sx={{
                        color: item.isInverse ? '#EB5757' : '#009161',
                        fontSize: 30,
                        marginRight: '3px',
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: '32px',
                      fontWeight: '600',
                      color: fontColor,
                    }}
                  >
                    {isNaN(Number(item.value))
                      ? item.value
                      : formatNumberToShortScale(item.value)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: '600',
                      marginTop: '12px',
                      marginLeft: '4px',
                      color: ThemePalette.typography.grey,
                    }}
                  >
                    {item.unit}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    marginTop: '8px',
                  }}
                >
                  <Typography
                    sx={{
                      color: ThemePalette.typography.grey,
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                    <span
                      style={{
                        color: item.isInverse
                          ? isNegativeDiff
                            ? '#009161'
                            : '#EB5757'
                          : isNegativeDiff
                          ? '#EB5757'
                          : '#009161',
                      }}
                    >
                      {Math.abs(item.diff)}%{' '}
                      {isNegativeDiff ? t('analysis_less') : t('analysis_more')}{' '}
                    </span>
                    {t('from_last_period')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default DashboardCardsView;
